// @flow
import * as React from 'react';
import ChannelList from 'containers/channels/ChannelList';
import {
  type Channel,
  isModerationChannel,
  isBrandEngageChannel,
  isBrandEngageBrandChannel,
  isBrandEngageRetailerChannel,
} from './ChannelInfo';
import { Switch, Route } from 'react-router-dom';
import type { ContextRouter } from 'react-router';
import { connect } from 'react-redux';
import {
  requestChannelStats,
  requestBrandEngageChannelStats,
} from './channelStatsActions';
import ManageButton from './ManageChannelAlertsButton';
import ModerationChannelContent from './ChannelContentCollection';
import BrandEngageChannelContent from 'containers/brand-engage/BrandEngageChannel';
import { EmptyCard } from 'components';
import { Map } from 'immutable';
import ChannelContentQueue from './ChannelContentQueue';
import { ClientContext } from 'containers/client/ClientProvider';
import { getUsePwrDistroEndpoints } from 'config/pwrDistroConfig';

import brandEngageChannelApi from 'containers/brand-engage/brandEngageSearchApi';
import moderationChannelApi from 'api/content/getChannelContent';

type OwnProps = {
  clientId: Id,
  channels: Array<Channel>,
  onChannelSelect?: Function,
  channelType?: string,
};
type ReduxStateProps = {
  stats: Map<string, number>,
  worklistStats: Map<string, number>,
};
type ReduxDispatchProps = {
  requestChannelStats: typeof requestChannelStats,
};
type Props = OwnProps & ReduxStateProps & ReduxDispatchProps & ContextRouter;
type State = {
  channelMap: Map<string, Channel>,
};

class ChannelListContainer extends React.Component<Props, State> {
  static contextType = ClientContext;

  state = {
    channelMap: Map(this.props.channels.map(ch => [ch.name, ch])),
  };

  static getDerivedStateFromProps(props) {
    return {
      channelMap: Map(props.channels.map(ch => [ch.name, ch])),
    };
  }

  requestChannelStats = (channelName: string) => {
    const channel = this.state.channelMap.get(channelName);

    if (channel) {
      this.props.requestChannelStats({
        channelName,
        clientId: this.props.clientId,
        channelType: this.props.channelType,
      });
    }
  };

  requestChannelQueueStats = (channelName: string) => {
    const channel = this.state.channelMap.get(channelName);

    if (channel) {
      this.props.requestChannelStats({
        channelName,
        clientId: this.props.clientId,
        worklist: true,
        channelType: this.props.channelType,
      });
    }
  };

  requestBrandEngageChannelStats = (channelName: string) => {
    const channel = this.state.channelMap.get(channelName);

    if (channel) {
      this.props.requestBrandEngageChannelStats({
        channelName,
        clientId: this.props.clientId,
        brandEngageType: channel.brandEngageType,
      });
    }
  };

  render() {
    const {
      match,
      stats,
      worklistStats,
      clientId,
      onChannelSelect,
      properties,
      channelType,
    } = this.props;

    const clientProps = this.context;
    const isPwrDistro =
      (channelType === 'Review' || channelType === 'Question') &&
      getUsePwrDistroEndpoints(clientProps);

    const clientBrandEngageTier = properties.get('BRAND_ENGAGE_TIER');
    const channelRecords = this.props.channels;
    const moderationChannels = Array.isArray(channelRecords)
      ? channelRecords.filter(channel => isModerationChannel(channel))
      : [];
    const brandEngageChannels = Array.isArray(channelRecords)
      ? channelRecords.filter(channel => isBrandEngageChannel(channel))
      : [];
    const shouldShowChannelListHeader =
      moderationChannels.length > 0 && brandEngageChannels.length > 0;
    const modChannelContentType =
      moderationChannels.length > 0 && moderationChannels[0].ugctype != null
        ? moderationChannels[0].ugctype
        : 'Your';
    const modChannelHeaderText = shouldShowChannelListHeader
      ? modChannelContentType + ' Content Channels'
      : null;
    const brandChannelHeaderText = shouldShowChannelListHeader
      ? 'Brand Engage Content Channels'
      : null;

    const [
      moderationChannelListItems,
      moderationChannelRoutes,
    ] = moderationChannels.reduce(
      (acc, ch) => {
        const hasWorklist = ch.worklist != null;
        const channelPath = makeChannelPath(match.url, ch);
        const channelWorklist = ch.worklist;
        const [items, routes] = acc;

        const item = (
          <ChannelList.Item
            label={ch.label}
            description={ch.description}
            key={ch.name}
            channelName={ch.name}
            path={channelPath}
            count={stats.get(ch.name)}
            ugctype={ch.contentUgctype}
            children={
              typeof onChannelSelect === 'function' && (
                <ManageButton
                  channelName={ch.label}
                  onClick={() => onChannelSelect(ch)}
                />
              )
            }
            requestChannelStats={this.requestChannelStats}
            hasWorklist={hasWorklist}
          />
        );
        const route = (
          <Route
            key={ch.name}
            exact
            path={channelPath}
            render={props => (
              <ModerationChannelContent
                clientId={clientId}
                channelName={ch.name}
                apiMethod={(token, endDateTime, pageNumber) =>
                  moderationChannelApi(token, {
                    clientId,
                    channelName: ch.name,
                    isPwrDistro,
                    ...(endDateTime && { endDateTime }),
                    ...(pageNumber != null && { pageNumber }),
                  })
                }
              />
            )}
          />
        );
        items.push(item);
        routes.push(route);

        if (channelWorklist) {
          const channelWorklistKey = `${ch.name}:worklist`;
          const channelWorklistPath = makeChannelWorkListPath(match.url, ch);

          const item = (
            <ChannelList.WorkListItem
              label={ch.label}
              description={channelWorklist}
              key={channelWorklistKey}
              channelName={ch.name}
              path={channelWorklistPath}
              count={worklistStats.get(ch.name)}
              requestChannelStats={this.requestChannelQueueStats}
            />
          );
          const route = (
            <Route
              key={channelWorklistKey}
              exact
              path={channelWorklistPath}
              render={() => (
                <ChannelContentQueue
                  clientId={clientId}
                  parentChannelName={ch.name}
                />
              )}
            />
          );
          items.push(item);
          routes.push(route);
        }

        return acc;
      },
      [[], []]
    );

    return (
      <Switch>
        <Route
          exact
          path={match.url}
          render={props => (
            <React.Fragment>
              {moderationChannelListItems.length > 0 && (
                <ChannelList header={modChannelHeaderText}>
                  {moderationChannelListItems}
                </ChannelList>
              )}
              {brandEngageChannels.length > 0 && (
                <ChannelList header={brandChannelHeaderText}>
                  {brandEngageChannels.map(ch => (
                    <ChannelList.Item
                      label={ch.label}
                      description={ch.description}
                      key={ch.name}
                      channelName={ch.name}
                      path={makeChannelPath(match.url, ch)}
                      count={stats.get(ch.name)}
                      children={
                        typeof onChannelSelect === 'function' && (
                          <ManageButton
                            channelName={ch.label}
                            onClick={() => onChannelSelect(ch)}
                          />
                        )
                      }
                      requestChannelStats={this.requestBrandEngageChannelStats}
                      ugctype={ch.contentUgctype}
                    />
                  ))}
                </ChannelList>
              )}
            </React.Fragment>
          )}
        />

        {moderationChannelRoutes}

        {brandEngageChannels.map(brandEngageChannel => (
          <Route
            key={brandEngageChannel.name}
            exact
            path={makeChannelPath(match.url, brandEngageChannel)}
            render={props => {
              if (isBrandEngageBrandChannel(brandEngageChannel)) {
                return (
                  <BrandEngageChannelContent
                    name={brandEngageChannel.name}
                    brandEngageType={brandEngageChannel.brandEngageType}
                    brandEngageTier={clientBrandEngageTier}
                    ugctype={brandEngageChannel.ugctype}
                    clientId={clientId}
                  />
                );
              }

              if (isBrandEngageRetailerChannel(brandEngageChannel)) {
                return (
                  <ModerationChannelContent
                    clientId={clientId}
                    channelName={brandEngageChannel.name}
                    apiMethod={token =>
                      brandEngageChannelApi(token, {
                        clientId,
                        channelName: brandEngageChannel.name,
                        brandEngageType: brandEngageChannel.brandEngageType,
                      })
                    }
                  />
                );
              }
            }}
          />
        ))}
        <Route
          render={() => (
            <EmptyCard
              title="Error loading channel"
              message="The channel you requested doesn't exist, or it might not be available to you. If you think this is an error, contact PowerReviews Support."
            />
          )}
        />
      </Switch>
    );
  }
}

function mapStateToProps(state: *) {
  return {
    stats: state.channelStats.stats,
    worklistStats: state.channelStats.worklistStats,
  };
}

export default connect(mapStateToProps, {
  requestChannelStats,
  requestBrandEngageChannelStats,
})(ChannelListContainer);

function makeChannelPath(matchUrl: string, channel: Channel): string {
  return matchUrl + '/' + channel.slug;
}

function makeChannelWorkListPath(matchUrl: string, channel: Channel): string {
  return makeChannelPath(matchUrl, channel) + '/worklist';
}



// WEBPACK FOOTER //
// ./src/containers/channels/ChannelListContainer.js